import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  MdAdd,
  MdBadge,
  MdDelete,
  MdEdit,
  MdFirstPage,
  MdLastPage,
  MdNavigateBefore,
  MdNavigateNext,
  MdViewList,
  MdViewModule,
  MdPerson,
  MdEmail,
  MdDateRange,
  MdSearch,
  MdFilterList,
  MdClose,
  MdExpandMore,
  MdExpandLess,
  MdVisibility,
} from "react-icons/md";

const CardSecretaires = ({
  isOpen,
  users,
  isLoading,
  onEdit,
  onDelete,
  onViewDetails,
}) => {
  // View mode: grid/list
  const [viewMode, setViewMode] = useState("grid");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [displayedUsers, setDisplayedUsers] = useState([]);

  // Search/filter
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  // Filtered users
  const getFilteredUsers = () => {
    let filtered = users;
    if (searchTerm.trim() !== "") {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          `${u.first_name || ""} ${u.last_name || ""}`
            .toLowerCase()
            .includes(lower) || u.email_user?.toLowerCase().includes(lower)
      );
    }
    return filtered;
  };

  // Update pagination when filtered/list/params change
  useEffect(() => {
    const filtered = getFilteredUsers();
    const total = Math.ceil(filtered.length / itemsPerPage) || 1;
    setTotalPages(total);

    let validPage = currentPage;
    if (currentPage > total) {
      validPage = 1;
      setCurrentPage(1);
    }
    const startIndex = (validPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedUsers(filtered.slice(startIndex, endIndex));
    // eslint-disable-next-line
  }, [users, currentPage, itemsPerPage, searchTerm]);

  // Avatar initials
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  // Pagination
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  const handleItemsPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };
  const getPageNumbers = () => {
    const pageNumbers = [];
    const delta = 2;
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pageNumbers.push(i);
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - delta; i <= currentPage + delta; i++)
          pageNumbers.push(i);
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };
  const renderPageButton = (pageNum, index) => {
    if (pageNum === "...") {
      return (
        <span
          key={`ellipsis-${index}`}
          className="flex h-10 w-10 items-center justify-center font-medium text-gray-500"
        >
          ...
        </span>
      );
    }
    return (
      <button
        key={pageNum}
        onClick={() => goToPage(pageNum)}
        className={`flex h-10 w-10 items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 ${
          currentPage === pageNum
            ? "scale-105 transform bg-[#57A863] text-white shadow-lg"
            : "border border-gray-300 bg-white text-gray-700 hover:border-[#57A863] hover:bg-gray-50 hover:text-[#57A863]"
        }`}
      >
        {pageNum}
      </button>
    );
  };
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = getPageNumbers();
    const filteredLength = getFilteredUsers().length;
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, filteredLength);

    return (
      <div className="mt-8 space-y-6">
        {/* Pagination Info */}
        <div className="flex flex-col items-center justify-between space-y-4 sm:flex-row sm:space-y-0">
          <div className="flex items-center space-x-4 text-sm text-gray-700">
            <div className="flex items-center space-x-2">
              <span className="font-medium">Lignes par page:</span>
              <select
                className="rounded-lg border border-gray-300 px-3 py-2 transition-all focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={18}>18</option>
                <option value={24}>24</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="hidden h-4 w-px bg-gray-300 sm:block"></div>
            <div className="text-gray-600">
              <span className="font-medium text-gray-900">{startItem}</span>
              {" - "}
              <span className="font-medium text-gray-900">{endItem}</span>
              {" sur "}
              <span className="font-semibold text-[#57A863]">
                {filteredLength}
              </span>
              {" entrées"}
            </div>
          </div>
          <div className="text-sm text-gray-600 sm:hidden">
            Page {currentPage} sur {totalPages}
          </div>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Première page"
          >
            <MdFirstPage className="h-5 w-5" />
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Page précédente"
          >
            <MdNavigateBefore className="h-5 w-5" />
          </button>
          <div className="hidden items-center space-x-1 sm:flex">
            {pageNumbers.map((pageNum, index) =>
              renderPageButton(pageNum, index)
            )}
          </div>
          <div className="flex items-center rounded-lg bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 sm:hidden">
            {currentPage} / {totalPages}
          </div>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Page suivante"
          >
            <MdNavigateNext className="h-5 w-5" />
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 transition-all hover:border-[#57A863] hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:bg-white"
            title="Dernière page"
          >
            <MdLastPage className="h-5 w-5" />
          </button>
        </div>
        {totalPages > 10 && (
          <div className="flex items-center justify-center space-x-2 text-sm">
            <span className="text-gray-600">Aller à la page:</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  goToPage(page);
                }
              }}
              className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-center focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
            />
            <span className="text-gray-600">sur {totalPages}</span>
          </div>
        )}
      </div>
    );
  };

  // Grid view
  const renderGridView = () => (
    <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3">
      {displayedUsers.map((user, index) => (
        <div
          key={user.id}
          className="group overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#57A863]/30 hover:shadow-xl"
        >
          <div className="bg-gradient-to-r from-[#57A863] to-[#3d9448] px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-lg font-semibold text-white">
                {getInitials(user.first_name, user.last_name)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold text-white">
                  {user.first_name} {user.last_name}
                </h3>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-sm">
                <MdEmail className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <span className="truncate font-medium text-gray-600">
                  {user.email_user}
                </span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <MdDateRange className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <span className="text-gray-600">
                  {new Date(user.created_at).toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-2">
              <button
                className="flex items-center justify-center rounded-lg bg-green-50 px-3 py-2.5 text-sm font-medium text-green-600 transition-all hover:scale-105 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                onClick={() => onViewDetails && onViewDetails(user)}
                title="Voir les détails"
              >
                <MdVisibility className="h-4 w-4" />
              </button>
              <button
                className="flex items-center justify-center rounded-lg bg-blue-50 px-3 py-2.5 text-sm font-medium text-blue-600 transition-all hover:scale-105 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                onClick={() => onEdit(user)}
                title="Modifier"
              >
                <MdEdit className="h-4 w-4" />
              </button>
              <button
                className="flex items-center justify-center rounded-lg bg-red-50 px-3 py-2.5 text-sm font-medium text-red-600 transition-all hover:scale-105 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                onClick={() => onDelete(user)}
                title="Supprimer"
              >
                <MdDelete className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // List view
  const renderListView = () => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th
                scope="col"
                className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                <div className="flex items-center space-x-1">
                  <MdPerson className="h-4 w-4" />
                  <span>Utilisateur</span>
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                <div className="flex items-center space-x-1">
                  <MdEmail className="h-4 w-4" />
                  <span>Email</span>
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                <div className="flex items-center space-x-1">
                  <MdDateRange className="h-4 w-4" />
                  <span>Créé le</span>
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {displayedUsers.map((user, index) => (
              <tr
                key={user.id}
                className={`transition-colors hover:bg-gray-50 ${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                }`}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#57A863] to-[#3d9448] text-sm font-semibold text-white">
                      {getInitials(user.first_name, user.last_name)}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {user.first_name} {user.last_name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="max-w-xs break-all text-sm text-gray-900">
                    {user.email_user}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {new Date(user.created_at).toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end space-x-2">
                    <button
                      className="inline-flex items-center rounded-lg bg-green-50 px-3 py-2 text-xs font-medium text-green-600 transition-colors hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                      onClick={() => onViewDetails && onViewDetails(user)}
                      title="Voir les détails"
                    >
                      <MdVisibility className="mr-1 h-3 w-3" />
                      Détails
                    </button>
                    <button
                      className="inline-flex items-center rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      onClick={() => onEdit(user)}
                      title="Modifier"
                    >
                      <MdEdit className="mr-1 h-3 w-3" />
                      Modifier
                    </button>
                    <button
                      className="inline-flex items-center rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      onClick={() => onDelete(user)}
                      title="Supprimer"
                    >
                      <MdDelete className="mr-1 h-3 w-3" />
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Empty state
  const renderEmptyState = () => (
    <div className="flex h-64 flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-8 text-center">
      <div className="rounded-full bg-gray-100 p-3">
        <MdBadge className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">
        Aucun(e) secrétaire trouvé(e)
      </h3>
      <p className="mt-2 max-w-sm text-sm text-gray-600">
        {searchTerm
          ? "Aucun résultat ne correspond à vos critères de recherche."
          : "Commencez par ajouter votre première secrétaire pour gérer le secrétariat."}
      </p>
      {!searchTerm && (
        <button
          onClick={isOpen}
          className="mt-6 flex items-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
        >
          <MdAdd className="mr-2 h-4 w-4" />
          Ajouter une secrétaire
        </button>
      )}
    </div>
  );

  // Loading state
  const renderLoadingState = () => (
    <div className="flex h-64 items-center justify-center rounded-xl border border-gray-200 bg-white">
      <div className="text-center">
        <div className="border-t-transparent mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#57A863]"></div>
        <p className="mt-4 text-lg font-medium text-gray-900">
          Chargement des secrétaires...
        </p>
        <p className="text-sm text-gray-500">Veuillez patienter</p>
      </div>
    </div>
  );

  // With data
  const renderContentWithData = () => (
    <>
      {viewMode === "grid" ? renderGridView() : renderListView()}
      {renderPagination()}
    </>
  );

  const getMainContent = () => {
    if (isLoading) return renderLoadingState();
    if (getFilteredUsers().length === 0) return renderEmptyState();
    return renderContentWithData();
  };

  // Search/filter controls
  const renderSearchAndFilterControls = () => (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex w-full items-center sm:w-auto">
          <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className="w-full rounded-md border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863] sm:w-80"
            placeholder="Recherche par nom ou email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setSearchTerm("")}
              title="Effacer la recherche"
              tabIndex={0}
            >
              <MdClose className="h-5 w-5" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilter(!showFilter)}
          className={`flex items-center rounded-md border px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#57A863] ${
            showFilter
              ? "border-[#57A863] bg-[#57A863] text-white"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          }`}
          title="Afficher les filtres"
        >
          <MdFilterList className="mr-2 h-5 w-5" />
          <span className="hidden sm:inline">Filtres avancés</span>
          <span className="sm:hidden">Filtres</span>
          {showFilter ? (
            <MdExpandLess className="ml-1 h-4 w-4" />
          ) : (
            <MdExpandMore className="ml-1 h-4 w-4" />
          )}
        </button>
      </div>
      {showFilter && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 transition-all duration-200">
          <div className="grid grid-cols-1 gap-4">
            {/* Future: add more filter fields here */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Actions
              </label>
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-md bg-[#57A863] px-3 py-2 text-sm font-medium text-white hover:bg-[#469354] focus:outline-none focus:ring-2 focus:ring-[#57A863]"
                  onClick={() => setCurrentPage(1)}
                >
                  Appliquer
                </button>
                <button
                  className="flex-1 rounded-md bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
          {searchTerm && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-gray-600">
                  Filtres actifs:
                </span>
                <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
                  Recherche: "{searchTerm}"
                  <button
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    onClick={() => setSearchTerm("")}
                  >
                    <MdClose className="h-3 w-3" />
                  </button>
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto space-y-6 px-4 py-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Secrétaires</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez les secrétaires et leurs informations
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View mode toggle */}
          <div className="flex overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
                viewMode === "grid"
                  ? "bg-[#57A863] text-white shadow-sm"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              title="Vue en grille"
            >
              <MdViewModule className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Grille</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
                viewMode === "list"
                  ? "bg-[#57A863] text-white shadow-sm"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              title="Vue en liste"
            >
              <MdViewList className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Liste</span>
            </button>
          </div>
          <button
            onClick={isOpen}
            className="flex items-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-3 py-2 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 sm:px-4"
          >
            <MdAdd className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Ajouter une secrétaire</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        </div>
      </div>
      {renderSearchAndFilterControls()}
      {getMainContent()}
    </div>
  );
};

CardSecretaires.propTypes = {
  isOpen: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func,
};

export default CardSecretaires;
